// docs/51 villages: the atlases the village view draws from (the world sheet, creatures, items) and
// the DOM sprites it moves: a creature frame, an item icon, a hand-drawn dish or critter. Shared by
// the view and the docs/116 inn layer.
import { loadWorldAtlas } from '../../components';
import { critterArt, dishArt, type CritterKind, type DishKind } from './critters';
import { CELL } from './layout';

// ---- atlases ------------------------------------------------------------------------------------
export interface Frame { x: number; y: number; w: number; h: number }
export type Atlas = { img: HTMLImageElement; frames: Record<string, Frame>; size: { w: number; h: number } };
const sheets = new Map<string, Promise<Atlas | undefined>>();
function loadSheet(name: 'creatures' | 'items'): Promise<Atlas | undefined> {
  let p = sheets.get(name);
  if (!p) {
    p = (async () => {
      try {
        const r = await fetch(`/assets/oryx/${name}.json`);
        if (!r.ok) return undefined;
        const j = (await r.json()) as { frames: Record<string, { frame: Frame }>; meta?: { size?: { w: number; h: number } } };
        const img = new Image();
        img.src = `/assets/oryx/${name}.png`;
        await img.decode();
        return { img, frames: Object.fromEntries(Object.entries(j.frames).map(([k, v]) => [k, v.frame])), size: j.meta?.size ?? { w: img.naturalWidth, h: img.naturalHeight } };
      } catch { return undefined; }
    })();
    sheets.set(name, p);
  }
  return p;
}
export type Sheets = { world: Atlas; creatures?: Atlas; items?: Atlas };
export async function loadAll(): Promise<Sheets | undefined> {
  const [world, creatures, items] = await Promise.all([loadWorldAtlas(), loadSheet('creatures'), loadSheet('items')]);
  if (!world) return undefined;
  return { world: { ...world, size: { w: world.img.naturalWidth, h: world.img.naturalHeight } }, ...(creatures ? { creatures } : {}), ...(items ? { items } : {}) };
}
export const creatureName = (n: number) => `creature_${String(n).padStart(3, '0')}`;
export const itemName = (n: number) => `item_${String(n).padStart(3, '0')}`;

// ---- sprites -----------------------------------------------------------------------------------
export function Sprite({ sheet, frame, scale, flip, className = '' }: { sheet: Atlas | undefined; frame: number; scale: number; flip?: boolean; className?: string }) {
  const f = sheet?.frames[creatureName(frame)];
  if (!sheet || !f) return <span className={`fk-village__sprite ${className}`} style={{ width: CELL * scale, height: CELL * scale }} />;
  return (
    <span className={`fk-village__sprite ${flip ? 'fk-village__sprite--flip' : ''} ${className}`} aria-hidden="true"
      style={{ width: f.w * scale, height: f.h * scale, backgroundImage: `url(${sheet.img.src})`, backgroundPosition: `-${f.x * scale}px -${f.y * scale}px`, backgroundSize: `${sheet.size.w * scale}px ${sheet.size.h * scale}px` }} />
  );
}
export function ItemIcon({ sheet, n, scale }: { sheet: Atlas | undefined; n: number; scale: number }) {
  const f = sheet?.frames[itemName(n)];
  if (!sheet || !f) return null;
  return <span className="fk-village__icon" aria-hidden="true" style={{ width: f.w * scale, height: f.h * scale, backgroundImage: `url(${sheet.img.src})`, backgroundPosition: `-${f.x * scale}px -${f.y * scale}px`, backgroundSize: `${sheet.size.w * scale}px ${sheet.size.h * scale}px` }} />;
}
/** docs/64: a hand-drawn dish (stew, bread, a mug) and a critter, pixel for pixel at the view's scale. */
export function Dish({ kind, left, scale }: { kind: DishKind; left: 0 | 1 | 2; scale: number }) {
  const a = dishArt(kind, left);
  return <span className="fk-village__pix" style={{ width: a.w * scale, height: a.h * scale, backgroundImage: `url(${a.url})`, backgroundSize: '100% 100%' }} />;
}
export function Critter({ kind, coat, frame, scale }: { kind: CritterKind; coat: number; frame: number; scale: number }) {
  const a = critterArt(kind, coat, frame);
  return <span className="fk-village__pix fk-village__critter" style={{ width: a.w * scale, height: a.h * scale, backgroundImage: `url(${a.url})`, backgroundSize: '100% 100%' }} />;
}

